import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Trash2, CheckCircle2, FileEdit } from "lucide-react";
import { api } from "../../api";
import { Badge } from "../ui/badge";
import DevisCreateModal from "./DevisCreateModal";

const STATUS_COLORS = {
  nouveau: "bg-[#e63946]/20 text-[#ff8c8c] border-[#e63946]/40",
  en_cours: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
  devis_envoye: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  clos: "bg-white/5 text-white/50 border-white/10",
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function QuotesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [creatingFor, setCreatingFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/quotes");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); api.post("/admin/quotes/mark-seen").catch(() => {}); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/quotes/${id}`, { status });
    toast.success("Statut mis à jour");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    await api.delete(`/admin/quotes/${id}`);
    toast.success("Supprimée");
    setSelected(null);
    load();
  };

  return (
    <div className="grid gap-6 lg:grid-cols-5" data-testid="quotes-list">
      <div className="rounded-lg border border-white/10 bg-[#0f0f0f] lg:col-span-3">
        <div className="border-b border-white/10 px-5 py-4">
          <h3 className="font-head text-lg font-medium uppercase tracking-tight text-white">Demandes reçues</h3>
          <p className="text-xs text-white/50">{items.length} demande(s)</p>
        </div>
        <div className="max-h-[70vh] divide-y divide-white/5 overflow-y-auto">
          {loading && <div className="p-6 text-sm text-white/50">Chargement…</div>}
          {!loading && items.length === 0 && (
            <div className="p-8 text-center text-sm text-white/50">Aucune demande pour le moment.</div>
          )}
          {items.map((q) => (
            <button
              key={q.id}
              onClick={() => setSelected(q)}
              className={`flex w-full items-start gap-3 px-5 py-4 text-left transition-colors ${selected?.id === q.id ? "bg-white/5" : "hover:bg-white/[0.03]"}`}
              data-testid={`quote-item-${q.id}`}
            >
              <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-gradient" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className="truncate font-medium text-white">{q.name}</p>
                  <span className="shrink-0 text-[10px] uppercase tracking-wider text-white/40">{fmtDate(q.created_at)}</span>
                </div>
                <p className="mt-0.5 truncate text-xs text-white/50">{q.category} · {q.service || "—"}</p>
                <p className="mt-1 truncate text-xs text-white/40">{q.message}</p>
              </div>
              <span className={`shrink-0 rounded border px-2 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_COLORS[q.status] || STATUS_COLORS.nouveau}`}>
                {q.status}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#0f0f0f] lg:col-span-2">
        {!selected ? (
          <div className="p-8 text-center text-sm text-white/50">Sélectionnez une demande pour voir le détail.</div>
        ) : (
          <div className="p-6" data-testid="quote-detail">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 className="font-head text-xl font-medium uppercase text-white">{selected.name}</h4>
                <p className="text-xs text-white/50">{fmtDate(selected.created_at)}</p>
              </div>
              <Badge variant="outline" className={`border ${STATUS_COLORS[selected.status] || STATUS_COLORS.nouveau}`}>
                {selected.status}
              </Badge>
            </div>

            <div className="mt-6 space-y-3 text-sm">
              <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-white/80 hover:text-white"><Mail size={14} className="text-[#ff4d4d]" /> {selected.email}</a>
              {selected.phone && <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-white/80 hover:text-white"><Phone size={14} className="text-[#ff4d4d]" /> {selected.phone}</a>}
              <p className="text-xs uppercase tracking-wider text-white/40">Catégorie</p>
              <p className="text-white">{selected.category} {selected.service ? `— ${selected.service}` : ""}</p>
              <p className="text-xs uppercase tracking-wider text-white/40">Message</p>
              <p className="whitespace-pre-line rounded-md border border-white/5 bg-black/40 p-3 text-white/80">{selected.message}</p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <button onClick={() => setStatus(selected.id, "en_cours")} className="rounded-md border border-white/10 px-3 py-2 text-xs text-white hover:bg-white/5" data-testid="quote-status-en_cours">En cours</button>
              <button onClick={() => setStatus(selected.id, "clos")} className="rounded-md border border-white/10 px-3 py-2 text-xs text-white hover:bg-white/5" data-testid="quote-status-clos"><CheckCircle2 size={12} className="mr-1 inline" />Clôturer</button>
              <button onClick={() => setCreatingFor(selected)} className="rounded-md bg-brand-gradient px-3 py-2 text-xs font-semibold text-white" data-testid="quote-create-devis"><FileEdit size={12} className="mr-1 inline" />Créer un devis</button>
              <button onClick={() => remove(selected.id)} className="rounded-md border border-red-500/30 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10"><Trash2 size={12} className="mr-1 inline" />Supprimer</button>
            </div>
          </div>
        )}
      </div>

      {creatingFor && (
        <DevisCreateModal
          open={!!creatingFor}
          onOpenChange={(v) => !v && setCreatingFor(null)}
          prefill={{
            client_name: creatingFor.name,
            client_email: creatingFor.email,
            client_phone: creatingFor.phone,
            project: creatingFor.service || creatingFor.category,
            quote_id: creatingFor.id,
          }}
          onCreated={() => { setCreatingFor(null); load(); toast.success("Devis créé"); }}
        />
      )}
    </div>
  );
}
